import { useEffect, useState, useMemo, useCallback } from 'react';
import { TramesaData } from '../data/tramesesMockData';

interface UseTramesesDataOptions {
  /** Excluir trameses con estat 'Finalitzat' */
  excludeFinalitzat?: boolean;
  /** Limitar el número de resultados */
  limit?: number;
}

/**
 * Hook para obtener y gestionar datos de Trameses.
 *
 * @param apiService - Servicio API que provee `GetTrameses()`
 * @param options - Opciones de filtrado y límite
 */
export function useTramesesData(
  apiService: { GetTrameses: (entityId: any) => Promise<TramesaData[]> },
  entityId: any,
  setSearchLoading: any,
  options?: UseTramesesDataOptions
) {
  const [data, setData] = useState<TramesaData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /** Función para cargar los datos */
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.GetTrameses(entityId);
      setData(result);
      setSearchLoading(false);
    } catch (err: any) {
      console.error('Error obtenint les trameses:', err);
      setError(err.message || 'Error desconegut al obtenir les trameses');
      setSearchLoading(false);
    } finally {
      setLoading(false);
    }
  }, [apiService]);

  /** Cargar al montar o cuando cambie el servicio */
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /** Aplicar filtros y límites */
  const filteredData = useMemo(() => {
    let filtered = [...data];

    if (options?.excludeFinalitzat) {
      filtered = filtered.filter(item => item.state !== 'Finalitzat');
    }

    if (options?.limit && options.limit > 0) {
      filtered = filtered.slice(0, options.limit);
    }

    return filtered;
  }, [data, options?.excludeFinalitzat, options?.limit]);

  return {
    data: filteredData,
    loading,
    error,
    refetch: fetchData,
  };
}
